import type {VadoCatalog} from "#pricing/catalogs/vado/catalog"
import type {KeyedVariants, PriceGraphData} from "#pricing/catalogs/graph-helpers"
import {and, createConditionedPriceNodes, createKeyedVariantPriceNodes, createPriceContainer, not, or, variantSelected} from "#pricing/catalogs/graph-helpers"
import {ConditionalAmountGroupNode} from "#pricing/nodes/conditional-amount-group-node"
import {ConditionalAmountList} from "#pricing/nodes/conditional-amount-list"
import {ConditionalAmountNode} from "#pricing/nodes/conditional-amount-node"
import {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import type {PriceContainer} from "#pricing/nodes/price-container"
import type {PricedItemNode} from "#pricing/nodes/priced-item-node"

/**
 * Same graph as createSafariPriceGraph (safari-graph.ts), built with VariantSelectedCondition instead of the deprecated
 * VariantConditionNode. Both exist in parallel until the stored graphs are migrated; they must evaluate identically.
 */
export function createSafariPriceGraphV2(catalog: VadoCatalog): PriceGraphData {
    const result: PriceGraphData = {
        amountGroups: [],
        pricedItemNodes: [],
        priceContainers: [],
    }

    //---------------------------------------------Configuration groups---------------------------------------------

    const VanityStyle = new ConfigurationGroupNode({
        parameters: {
            groupId: "1bcac78c-e813-4df5-92ad-7d01f1675693",
            groupName: "",
            currentVariant: {
                groupId: "1bcac78c-e813-4df5-92ad-7d01f1675693",
                groupName: "",
                variantId: "f2a6cc30-218f-4962-b2d1-2231433bae99",
                variantName: "",
            },
            allVariants: [
                {groupId: "1bcac78c-e813-4df5-92ad-7d01f1675693", groupName: "", variantId: "fluted", variantName: ""},
                {groupId: "1bcac78c-e813-4df5-92ad-7d01f1675693", groupName: "", variantId: "smooth", variantName: ""},
            ],
        },
    })

    const VanitySize = new ConfigurationGroupNode({
        parameters: {
            groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae",
            groupName: "",
            currentVariant: {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "400_left", variantName: ""},
            allVariants: [
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "400_left", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "400_right", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "500", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "600", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "800", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "1000", variantName: ""},
                {groupId: "9c3eeaa2-5063-46fd-bc90-541a303d91ae", groupName: "", variantId: "1200", variantName: ""},
            ],
        },
    })

    const VanityMount = new ConfigurationGroupNode({
        parameters: {
            groupId: "mount",
            groupName: "",
            currentVariant: {groupId: "mount", groupName: "", variantId: "wall_hung", variantName: ""},
            allVariants: [
                {groupId: "mount", groupName: "", variantId: "wall_hung", variantName: ""},
                {groupId: "mount", groupName: "", variantId: "floor_mounted", variantName: ""},
            ],
        },
    })

    const HandleType = new ConfigurationGroupNode({
        parameters: {
            groupId: "handle",
            groupName: "",
            currentVariant: {groupId: "handle", groupName: "", variantId: "knob", variantName: ""},
            allVariants: [
                {groupId: "handle", groupName: "", variantId: "knob", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "knob_fluted", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "knob_small", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "bar", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "top_mount", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "pull_handle", variantName: ""},
            ],
        },
    })

    const HandleFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "handle_comb_finish",
            groupName: "",
            currentVariant: {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_chrome", variantName: ""},
            allVariants: [
                {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_chrome", variantName: ""},
                {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_matt_black", variantName: ""},
                {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_satin_brass", variantName: ""},
                {groupId: "handle_comb_finish", groupName: "", variantId: "handle_comb_pewter", variantName: ""},
            ],
        },
    })

    const BasinWaste = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_waste",
            groupName: "",
            currentVariant: {groupId: "basin_waste", groupName: "", variantId: "comb_with_waste", variantName: ""},
            allVariants: [
                {groupId: "basin_waste", groupName: "", variantId: "comb_with_waste", variantName: ""},
                {groupId: "basin_waste", groupName: "", variantId: "comb_without_waste", variantName: ""},
            ],
        },
    })

    const TapFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "tap_comb_finish",
            groupName: "",
            currentVariant: {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_chrome", variantName: ""},
            allVariants: [
                {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_chrome", variantName: ""},
                {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_matt_black", variantName: ""},
                {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_satin_brass", variantName: ""},
                {groupId: "tap_comb_finish", groupName: "", variantId: "tap_comb_pewter", variantName: ""},
            ],
        },
    })

    const LeverOption = new ConfigurationGroupNode({
        parameters: {
            groupId: "lever_option_handle",
            groupName: "",
            currentVariant: {groupId: "lever_option_handle", groupName: "", variantId: "lever_handle", variantName: ""},
            allVariants: [
                {groupId: "lever_option_handle", groupName: "", variantId: "lever_handle", variantName: ""},
                {groupId: "lever_option_handle", groupName: "", variantId: "leverless_handle", variantName: ""},
            ],
        },
    })

    const BottleTrap = new ConfigurationGroupNode({
        parameters: {
            groupId: "bottle_trap",
            groupName: "",
            currentVariant: {groupId: "bottle_trap", groupName: "", variantId: "bottle_trap_with", variantName: ""},
            allVariants: [
                {groupId: "bottle_trap", groupName: "", variantId: "bottle_trap_with", variantName: ""},
                {groupId: "bottle_trap", groupName: "", variantId: "bottle_trap_without", variantName: ""},
            ],
        },
    })

    const BottleTrapFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "bottle_trap_finish",
            groupName: "",
            currentVariant: {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_chrome", variantName: ""},
            allVariants: [
                {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_chrome", variantName: ""},
                {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_matt_black", variantName: ""},
                {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_satin_brass", variantName: ""},
                {groupId: "bottle_trap_finish", groupName: "", variantId: "bottle_trap_pewter", variantName: ""},
            ],
        },
    })

    const VanityColourWithNaturalOak = new ConfigurationGroupNode({
        parameters: {
            groupId: "furniture_finish",
            groupName: "",
            currentVariant: {groupId: "furniture_finish", groupName: "", variantId: "arctic_white", variantName: ""},
            allVariants: [
                {groupId: "furniture_finish", groupName: "", variantId: "arctic_white", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "sage_green", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "charcoal_grey", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "atlantic_blue", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "terracotta", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "cashmere", variantName: ""},
                {groupId: "furniture_finish", groupName: "", variantId: "natural_oak", variantName: ""},
            ],
        },
    })

    const BasinDesign400_500 = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_400-500",
            groupName: "",
            currentVariant: {groupId: "basin_400-500", groupName: "", variantId: "mineral_400-500", variantName: ""},
            allVariants: [
                {groupId: "basin_400-500", groupName: "", variantId: "mineral_400-500", variantName: ""},
                {groupId: "basin_400-500", groupName: "", variantId: "ceramic_400-500", variantName: ""},
            ],
        },
    })

    const BasinOverflowFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "overflow_finish",
            groupName: "",
            currentVariant: {groupId: "overflow_finish", groupName: "", variantId: "overflow_chrome", variantName: ""},
            allVariants: [
                {groupId: "overflow_finish", groupName: "", variantId: "overflow_chrome", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "overflow_matt_black", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "overflow_satin_brass", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "overflow_pewter", variantName: ""},
            ],
        },
    })

    const BasinDesign600_800 = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_600-800",
            groupName: "",
            currentVariant: {groupId: "basin_600-800", groupName: "", variantId: "mineral_600-800", variantName: ""},
            allVariants: [
                {groupId: "basin_600-800", groupName: "", variantId: "mineral_600-800", variantName: ""},
                {groupId: "basin_600-800", groupName: "", variantId: "ceramic_600-800", variantName: ""},
                {groupId: "basin_600-800", groupName: "", variantId: "countertop_600-800", variantName: ""},
            ],
        },
    })

    const TapMineralInset = new ConfigurationGroupNode({
        parameters: {
            groupId: "tap_mineral_inset",
            groupName: "",
            currentVariant: {groupId: "tap_mineral_inset", groupName: "", variantId: "100/200", variantName: ""},
            allVariants: [
                {groupId: "tap_mineral_inset", groupName: "", variantId: "100/200", variantName: ""},
                {groupId: "tap_mineral_inset", groupName: "", variantId: "101/201", variantName: ""},
                {groupId: "tap_mineral_inset", groupName: "", variantId: "102/202", variantName: ""},
            ],
        },
    })

    const BasinDesign1000_1200 = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_1000-1200",
            groupName: "",
            currentVariant: {groupId: "basin_1000-1200", groupName: "", variantId: "mineral_1000-1200", variantName: ""},
            allVariants: [
                {groupId: "basin_1000-1200", groupName: "", variantId: "mineral_1000-1200", variantName: ""},
                {groupId: "basin_1000-1200", groupName: "", variantId: "countertop_1000-1200", variantName: ""},
            ],
        },
    })

    const BasinFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_finish_mineral",
            groupName: "",
            currentVariant: {groupId: "basin_finish_mineral", groupName: "", variantId: "mineral_arctic_white", variantName: ""},
            allVariants: [
                {groupId: "basin_finish_mineral", groupName: "", variantId: "mineral_arctic_white", variantName: ""},
                {groupId: "basin_finish_mineral", groupName: "", variantId: "mineral_gross_white", variantName: ""},
            ],
        },
    })

    const TapCounter = new ConfigurationGroupNode({
        parameters: {
            groupId: "tap_counter",
            groupName: "",
            currentVariant: {groupId: "tap_counter", groupName: "", variantId: "100e/200e", variantName: ""},
            allVariants: [
                {groupId: "tap_counter", groupName: "", variantId: "100e/200e", variantName: ""},
                {groupId: "tap_counter", groupName: "", variantId: "109/209", variantName: ""},
                {groupId: "tap_counter", groupName: "", variantId: "109s/209s", variantName: ""},
                {groupId: "tap_counter", groupName: "", variantId: "109s-sq/209s-sq", variantName: ""},
            ],
        },
    })

    const BasinShape = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin_shape",
            groupName: "",
            currentVariant: {groupId: "basin_shape", groupName: "", variantId: "round", variantName: ""},
            allVariants: [
                {groupId: "basin_shape", groupName: "", variantId: "round", variantName: ""},
                {groupId: "basin_shape", groupName: "", variantId: "round_fluted", variantName: ""},
                {groupId: "basin_shape", groupName: "", variantId: "square", variantName: ""},
            ],
        },
    })

    const WorktopFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "worktop_finish",
            groupName: "",
            currentVariant: {groupId: "worktop_finish", groupName: "", variantId: "wt_gross_white", variantName: ""},
            allVariants: [
                {groupId: "worktop_finish", groupName: "", variantId: "wt_gross_white", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "wt_arctic_white", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "wt_sage_green", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "wt_charcoal_grey", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "arctic_blue", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "terracotta", variantName: ""},
                {groupId: "worktop_finish", groupName: "", variantId: "cashmere", variantName: ""},
                //quirk: added by the finish map ("Atlantic Blue"), the configurator variant is "arctic_blue"
                {groupId: "worktop_finish", groupName: "", variantId: "atlantic_blue", variantName: ""},
            ],
        },
    })

    const BasinFinishCountertop = new ConfigurationGroupNode({
        parameters: {
            groupId: "countertop_basin_finish",
            groupName: "",
            currentVariant: {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_gross_white", variantName: ""},
            allVariants: [
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_gross_white", variantName: ""},
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_arctic_white", variantName: ""},
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_sage_green", variantName: ""},
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_desert_grey", variantName: ""},
                //quirk: added by the finish map ("Cashmere"), not a configurator variant
                {groupId: "countertop_basin_finish", groupName: "", variantId: "ct_basin_cashmere", variantName: ""},
            ],
        },
    })

    const BasinOverflow = new ConfigurationGroupNode({
        parameters: {
            groupId: "overflow",
            groupName: "",
            currentVariant: {groupId: "overflow", groupName: "", variantId: "with_overflow", variantName: ""},
            allVariants: [
                {groupId: "overflow", groupName: "", variantId: "with_overflow", variantName: ""},
                {groupId: "overflow", groupName: "", variantId: "without_oferflow", variantName: ""},
            ],
        },
    })

    //---------------------------------------------Keyed variants (catalog key → config variant)---------------------------------------------

    const keyed = (group: ConfigurationGroupNode, entries: [string, string][]): KeyedVariants => ({groups: [group], variantIdByKey: new Map(entries)})

    const vanityColorWithNaturalOakVariants = keyed(VanityColourWithNaturalOak, [
        ["Arctic White", "arctic_white"],
        ["Sage Green", "sage_green"],
        ["Charcoal Grey", "charcoal_grey"],
        ["Atlantic Blue", "atlantic_blue"],
        ["Terracotta", "terracotta"],
        ["Cashmere", "cashmere"],
        ["Natural Oak", "natural_oak"],
    ])

    const vanityColorVariants = keyed(VanityColourWithNaturalOak, [
        ["Arctic White", "arctic_white"],
        ["Sage Green", "sage_green"],
        ["Charcoal Grey", "charcoal_grey"],
        ["Atlantic Blue", "atlantic_blue"],
        ["Terracotta", "terracotta"],
        ["Cashmere", "cashmere"],
    ])

    const handleFinishVariants = keyed(HandleFinish, [
        ["Chrome", "handle_comb_chrome"],
        ["Matt Black", "handle_comb_matt_black"],
        ["Satin Brass", "handle_comb_satin_brass"],
        ["Pewter", "handle_comb_pewter"],
    ])

    const bottleTrapFinishVariants = keyed(BottleTrapFinish, [
        ["Chrome", "bottle_trap_chrome"],
        ["Matt Black", "bottle_trap_matt_black"],
        ["Satin Brass", "bottle_trap_satin_brass"],
        ["Pewter", "bottle_trap_pewter"],
    ])

    const tapFinishVariants = keyed(TapFinish, [
        ["Chrome", "tap_comb_chrome"],
        ["Matt Black", "tap_comb_matt_black"],
        ["Satin Brass", "tap_comb_satin_brass"],
        ["Pewter", "tap_comb_pewter"],
    ])

    const overflowCoverFinishVariants = keyed(BasinOverflowFinish, [
        ["Chrome", "overflow_chrome"],
        ["Matt Black", "overflow_matt_black"],
        ["Satin Brass", "overflow_satin_brass"],
        ["Pewter", "overflow_pewter"],
    ])

    const basinFinishVariants = keyed(BasinFinish, [
        ["Arctic White", "mineral_arctic_white"],
        ["Gloss White", "mineral_gross_white"],
    ])

    const basinFinishCountertopVariants = keyed(BasinFinishCountertop, [
        ["Arctic White", "ct_basin_arctic_white"],
        ["Gloss White", "ct_basin_gross_white"],
        ["Sage Green", "ct_basin_sage_green"],
        ["Cashmere", "ct_basin_cashmere"], //quirk: not a configurator variant (the configurator offers ct_basin_desert_grey)
    ])

    const worktopFinishVariants = keyed(WorktopFinish, [
        ["Gloss White", "wt_gross_white"],
        ["Arctic White", "wt_arctic_white"],
        ["Sage Green", "wt_sage_green"],
        ["Charcoal Grey", "wt_charcoal_grey"],
        ["Atlantic Blue", "atlantic_blue"], //quirk: the configurator variant is "arctic_blue"
        ["Terracotta", "terracotta"],
        ["Cashmere", "cashmere"],
    ])

    //---------------------------------------------Handle amount---------------------------------------------

    const handleAmount = (description: string, amount: number, size: string, mount: string) =>
        new ConditionalAmountNode({
            parameters: {description, amount, condition: and(variantSelected(VanitySize, size), variantSelected(VanityMount, mount))},
        })

    const handleAmountGroup = new ConditionalAmountGroupNode({
        parameters: {
            defaultAmount: 1,
            description: "Handle amount",
            amountNodes: new ConditionalAmountList({
                parameters: {
                    list: [
                        handleAmount("Handle: 2/500", 2, "500", "floor_mounted"),
                        handleAmount("Handle: 2/600", 2, "600", "floor_mounted"),
                        handleAmount("Handle: 2/800", 2, "800", "floor_mounted"),
                        handleAmount("Handle: 2/1000", 2, "1000", "wall_hung"),
                        handleAmount("Handle: 2/1200", 2, "1200", "wall_hung"),
                        handleAmount("Handle: 4/1000", 4, "1000", "floor_mounted"),
                        handleAmount("Handle: 4/1200", 4, "1200", "floor_mounted"),
                    ],
                },
            }),
        },
    })
    result.amountGroups.push(handleAmountGroup)

    //---------------------------------------------Mixer & basin amount---------------------------------------------

    const mixerAmountGroup = new ConditionalAmountGroupNode({
        parameters: {
            defaultAmount: 1,
            description: "Mixer amount",
            amountNodes: new ConditionalAmountList({
                parameters: {
                    list: [new ConditionalAmountNode({parameters: {description: "Lever: 2", amount: 2, condition: variantSelected(VanitySize, ["1200", "1000"])}})],
                },
            }),
        },
    })
    result.amountGroups.push(mixerAmountGroup)

    //---------------------------------------------Handles (shared across all units with different amounts)---------------------------------------------

    const createHandles = (): PriceContainer => {
        const handle = (description: string, terms: string, variantId: string, termExclude?: string[]) =>
            createPriceContainer(
                description,
                description,
                createKeyedVariantPriceNodes(catalog, terms, 4, handleFinishVariants, {amount: handleAmountGroup, termExclude}),
                variantSelected(HandleType, variantId),
            )

        //only for grouping
        return createPriceContainer(
            "Handles",
            "Handles",
            [
                handle("Handle knob", "Safari Smooth Knob Handle", "knob", ["Small"]),
                handle("Handle knob fluted", "Safari Fluted Knob Handle", "knob_fluted"),
                handle("Handle knob small", "Safari Small Smooth Knob Handle", "knob_small"),
                handle("Handle bar", "Safari Bar Handle", "bar"),
                handle("Handle top mount", "Safari Top Mount Handle", "top_mount"),
                handle("Handle fluted pull", "Safari Fluted Pull Handle", "pull_handle"),
            ],
            variantSelected(VanitySize, ["400_left", "400_right", "500", "600", "800", "1000", "1200"]),
        )
    }

    //---------------------------------------------400mm---------------------------------------------

    const create400Container = (): PriceContainer => {
        const unit = (description: string, terms: string, style: "smooth" | "fluted", mount: string, size: string) =>
            createPriceContainer(
                description,
                description,
                style === "smooth"
                    ? createKeyedVariantPriceNodes(catalog, terms, 6, vanityColorVariants)
                    : createKeyedVariantPriceNodes(catalog, terms, 7, vanityColorWithNaturalOakVariants),
                and(variantSelected(VanityMount, mount), variantSelected(VanityStyle, style), variantSelected(VanitySize, size)),
            )

        return createPriceContainer(
            "400mm",
            "400mm",
            [
                unit("Floor/Smooth", "Safari 400mm Smooth Cloakroom Unit - floor mounted, Left Pull", "smooth", "floor_mounted", "400_left"),
                unit("Floor/Smooth", "Safari 400mm Smooth Cloakroom Unit - floor mounted, Right Pull", "smooth", "floor_mounted", "400_right"),
                unit("Wall/Smooth", "Safari 400mm Smooth Cloakroom Unit - wall hung, Left Pull", "smooth", "wall_hung", "400_left"),
                unit("Wall/Smooth", "Safari 400mm Smooth Cloakroom Unit - wall hung, Right Pull", "smooth", "wall_hung", "400_right"),
                unit("Floor/Fluted/Left", "Safari 400mm Fluted Cloakroom unit - floor mounted, Left Pull", "fluted", "floor_mounted", "400_left"),
                unit("Floor/Fluted/Right", "Safari 400mm Fluted Cloakroom unit - floor mounted, Right Pull", "fluted", "floor_mounted", "400_right"),
                unit("Wall/Fluted/Left", "Safari 400mm Fluted Cloakroom unit - wall hung, Left Pull", "fluted", "wall_hung", "400_left"),
                unit("Wall/Fluted/Right", "Safari 400mm Fluted Cloakroom unit - wall hung, Right Pull", "fluted", "wall_hung", "400_right"),
                ...createConditionedPriceNodes(catalog, "400mm Ceramic Cloakroom Basin", 1, variantSelected(BasinDesign400_500, "ceramic_400-500")),
                ...createConditionedPriceNodes(catalog, "400mm Mineral Cast Cloakroom Basin", 1, variantSelected(BasinDesign400_500, "mineral_400-500")),
            ],
            variantSelected(VanitySize, ["400_left", "400_right"]),
        )
    }

    //---------------------------------------------500/600/800mm building blocks---------------------------------------------

    /** Floor/wall containers, each split into smooth/fluted. */
    const createFurniture = (size: string, smoothVariants: KeyedVariants, amount?: ConditionalAmountGroupNode): PriceContainer[] => {
        const styles = (mount: string) => [
            createPriceContainer(
                "Smooth",
                "Smooth",
                createKeyedVariantPriceNodes(catalog, `Safari ${size}mm Smooth Vanity Unit - ${mount}`, 6, smoothVariants, {amount}),
                variantSelected(VanityStyle, "smooth"),
            ),
            createPriceContainer(
                "Fluted",
                "Fluted",
                createKeyedVariantPriceNodes(catalog, `Safari ${size}mm Fluted Vanity Unit - ${mount}`, 7, vanityColorWithNaturalOakVariants, {amount}),
                variantSelected(VanityStyle, "fluted"),
            ),
        ]
        return [
            createPriceContainer("Floor", "Floor", styles("floor mounted"), variantSelected(VanityMount, "floor_mounted")),
            createPriceContainer("Wall", "Wall", styles("wall hung"), variantSelected(VanityMount, "wall_hung")),
        ]
    }

    const createBasins = (size: string, basinDesign: ConfigurationGroupNode, ceramicId: string, mineralId: string): PricedItemNode[] => [
        ...createConditionedPriceNodes(catalog, `${size}mm Ceramic Vanity Basin`, 1, variantSelected(basinDesign, ceramicId)),
        ...createConditionedPriceNodes(catalog, `${size}mm Mineral Cast Vanity Basin`, 1, variantSelected(basinDesign, mineralId)),
    ]

    const createWorktop = (size: string, basinDesign: ConfigurationGroupNode, countertopId: string): PriceContainer =>
        createPriceContainer(
            "Worktop",
            "Worktop",
            createKeyedVariantPriceNodes(catalog, `Safari ${size}mm Mineral Worktop`, 7, worktopFinishVariants),
            variantSelected(basinDesign, countertopId),
        )

    //---------------------------------------------500/600/800mm---------------------------------------------

    //also used for the 1000 version (twice)
    const furniture500 = createFurniture("500", vanityColorVariants, mixerAmountGroup)

    const create500Container = (): PriceContainer =>
        createPriceContainer(
            "500mm",
            "500mm",
            [...furniture500, ...createBasins("500", BasinDesign400_500, "ceramic_400-500", "mineral_400-500")],
            variantSelected(VanitySize, "500"),
        )

    //also used for the 1200 version (twice)
    const furniture600 = createFurniture("600", vanityColorVariants, mixerAmountGroup)

    const create600Container = (): PriceContainer =>
        createPriceContainer(
            "600mm",
            "600mm",
            [
                ...furniture600,
                ...createBasins("600", BasinDesign600_800, "ceramic_600-800", "mineral_600-800"),
                createWorktop("600", BasinDesign600_800, "countertop_600-800"),
            ],
            variantSelected(VanitySize, "600"),
        )

    const create800Container = (): PriceContainer =>
        createPriceContainer(
            "800mm",
            "800mm",
            [
                //quirk: the smooth 800mm units use the keys including Natural Oak (only 6 items exist, so that key is unused)
                ...createFurniture("800", vanityColorWithNaturalOakVariants),
                ...createBasins("800", BasinDesign600_800, "ceramic_600-800", "mineral_600-800"),
                createWorktop("800", BasinDesign600_800, "countertop_600-800"),
            ],
            variantSelected(VanitySize, "800"),
        )

    //---------------------------------------------1000/1200mm connector---------------------------------------------

    //TODO: the joining screws are missing in the October 2026 price list; add createScrews() to the price containers again once they are back
    const createScrews = (): PriceContainer => {
        const condition = variantSelected(VanitySize, ["1000", "1200"])
        const link = createConditionedPriceNodes(catalog, "Safari Furniture Joining Screws For use with 500mm and 600mm units", 1, condition)
        return createPriceContainer("Joining Screws", "Joining Screws", link, condition)
    }

    //---------------------------------------------1000/1200mm---------------------------------------------

    const create1000Container = (): PriceContainer =>
        createPriceContainer(
            "1000mm",
            "1000mm",
            [
                ...furniture500,
                createWorktop("1000", BasinDesign1000_1200, "countertop_1000-1200"),
                ...createKeyedVariantPriceNodes(catalog, "1000mm Mineral Cast Double Vanity Basin", 2, basinFinishVariants),
            ],
            variantSelected(VanitySize, "1000"),
        )

    const create1200Container = (): PriceContainer =>
        createPriceContainer(
            "1200mm",
            "1200mm",
            [
                ...furniture600,
                createWorktop("1200", BasinDesign1000_1200, "countertop_1000-1200"),
                ...createKeyedVariantPriceNodes(catalog, "1200mm Mineral Cast Double Vanity Basin", 2, basinFinishVariants),
            ],
            variantSelected(VanitySize, "1200"),
        )

    //---------------------------------------------Mixer building blocks---------------------------------------------

    type WasteItems = {noWaste: PricedItemNode[]; withWaste: PricedItemNode[]}

    /** Lever/leverless containers, each split into without/with basin waste. */
    const leverWasteContainers = (lever: WasteItems, leverless: WasteItems): PriceContainer[] => {
        const waste = (items: WasteItems) => [
            createPriceContainer("No Waste", "No Waste", items.noWaste, variantSelected(BasinWaste, "comb_without_waste")),
            createPriceContainer("With Waste", "With Waste", items.withWaste, variantSelected(BasinWaste, "comb_with_waste")),
        ]
        return [
            createPriceContainer("Lever", "Lever", waste(lever), variantSelected(LeverOption, "lever_handle")),
            createPriceContainer("Leverless", "Leverless", waste(leverless), variantSelected(LeverOption, "leverless_handle")),
        ]
    }

    const leverContainers = (lever: PricedItemNode[], leverless: PricedItemNode[]): PriceContainer[] => [
        createPriceContainer("Lever", "Lever", lever, variantSelected(LeverOption, "lever_handle")),
        createPriceContainer("Leverless", "Leverless", leverless, variantSelected(LeverOption, "leverless_handle")),
    ]

    const tapNodes = (terms: string, amount?: ConditionalAmountGroupNode, termExclude?: string[]) =>
        createKeyedVariantPriceNodes(catalog, terms, 4, tapFinishVariants, {amount, termExclude})

    //---------------------------------------------400 mini mixers---------------------------------------------

    const createMiniMixers = (): PriceContainer =>
        createPriceContainer(
            "Mini Mixer",
            "Mini Mixer",
            leverWasteContainers(
                {
                    noWaste: tapNodes("Safari Lever Cold Start Mini Mono Basin Mixer", undefined, ["Basin Waste"]),
                    withWaste: tapNodes("Safari Lever Cold Start Mini Mono Basin Mixer + Universal Basin Waste"),
                },
                {
                    noWaste: tapNodes("Safari Leverless Cold Start Mini Mono Basin Mixer", undefined, ["Basin Waste"]),
                    withWaste: tapNodes("Safari Leverless Cold Start Mini Mono Basin Mixer + Universal Basin Waste"),
                },
            ),
            variantSelected(VanitySize, ["400_left", "400_right"]),
        )

    //---------------------------------------------Basin waste---------------------------------------------

    const createUniversalBasinWaste = (): PriceContainer => {
        const cUniversalBasinWaste = createPriceContainer(
            "Universal Basin Waste",
            "Universal Basin Waste",
            tapNodes("Universal Basin Waste", mixerAmountGroup, ["Mixer", "Matt White"]), //Matt White is not offered for Safari
            variantSelected(BasinWaste, "comb_with_waste"),
        )

        return createPriceContainer(
            "Relevant Configurations Basin Waste",
            "Relevant Configurations Basin Waste",
            [cUniversalBasinWaste],
            not(
                or(
                    variantSelected(VanitySize, ["400_left", "400_right", "500"]),
                    variantSelected(TapMineralInset, "100/200"),
                    variantSelected(BasinDesign600_800, "ceramic_600-800"),
                ),
            ),
        )
    }

    //---------------------------------------------500/600/800/1000/1200mm mixers---------------------------------------------

    const createMixers = (): PriceContainer => {
        const cDeckMountedMono = createPriceContainer(
            "Deck Mounted Mono",
            "Deck Mounted Mono",
            leverWasteContainers(
                {
                    noWaste: tapNodes("Safari Lever Cold Start Mono Basin Mixer", mixerAmountGroup, ["Basin Waste", "Pressure"]),
                    withWaste: tapNodes("Safari Lever Cold Start Mono Basin Mixer + Universal Basin Waste", mixerAmountGroup, ["Pressure"]),
                },
                {
                    noWaste: tapNodes("Safari Leverless Cold Start Mono Basin Mixer", mixerAmountGroup, ["Basin Waste", "Pressure"]),
                    withWaste: tapNodes("Leverless Cold Start Mono Basin Mixer + Universal Basin Waste", mixerAmountGroup, ["Pressure"]),
                },
            ),
            or(variantSelected(TapMineralInset, "100/200"), variantSelected(BasinDesign600_800, "ceramic_600-800"), variantSelected(VanitySize, "500")),
        )

        const cThreeHoleDeckMounted = createPriceContainer(
            "Three Hole Deck Mounted",
            "Three Hole Deck Mounted",
            leverContainers(
                tapNodes("Safari Lever Three Hole Deck Mounted Basin Mixer", mixerAmountGroup, ["Spout", "Pressure"]),
                tapNodes("Safari Leverless Three Hole Deck Mounted Basin Mixer", mixerAmountGroup, ["Spout", "Pressure"]),
            ),
            variantSelected(TapMineralInset, "101/201"),
        )

        const cThreeHoleDeckMountedSpout = createPriceContainer(
            "Three Hole Deck Mounted Spout",
            "Three Hole Deck Mounted Spout",
            leverContainers(
                tapNodes("Safari Lever Three Hole Deck Mounted Basin Mixer with Pull Out Spout", mixerAmountGroup),
                tapNodes("Safari Leverless Three Hole Deck Mounted Basin Mixer with Pull Out Spout", mixerAmountGroup),
            ),
            variantSelected(TapMineralInset, "102/202"),
        )

        return createPriceContainer(
            "Mixers",
            "Mixers",
            [cDeckMountedMono, cThreeHoleDeckMounted, cThreeHoleDeckMountedSpout],
            variantSelected(VanitySize, ["500", "600", "800", "1000", "1200"]),
        )
    }

    //---------------------------------------------600/800/1000/1200mm countertop mixers and basins---------------------------------------------

    const countertopSelected = () =>
        or(variantSelected(BasinDesign600_800, "countertop_600-800"), variantSelected(BasinDesign1000_1200, "countertop_1000-1200"))

    const createCountertopMixers = (): PriceContainer => {
        const cExtendedMixers = createPriceContainer(
            "Extended Mixers",
            "Extended Mixers",
            leverContainers(
                tapNodes("Safari Lever Cold Start Extended Mono Basin Mixer", mixerAmountGroup, ["Pressure"]),
                tapNodes("Safari Leverless Cold Start Extended Mono Basin Mixer", mixerAmountGroup, ["Pressure"]),
            ),
            variantSelected(TapCounter, "100e/200e"),
        )

        const cWallMounted3Hole = createPriceContainer(
            "Wall Mounted 3 Hole",
            "Wall Mounted 3 Hole",
            leverContainers(
                tapNodes("Safari Lever Three Hole Wall Mounted Basin Mixer", mixerAmountGroup, ["Pressure"]),
                tapNodes("Safari Leverless Three Hole Wall Mounted Basin Mixer", mixerAmountGroup, ["Pressure"]),
            ),
            variantSelected(TapCounter, "109/209"),
        )

        //This one according to the requirements should always have a lever.
        const cWallMounted2Hole = createPriceContainer(
            "Wall Mounted 2 Hole",
            "Wall Mounted 2 Hole",
            tapNodes("Safari Lever Wall Mounted Basin Mixer", mixerAmountGroup, ["Square", "Pressure"]),
            variantSelected(TapCounter, "109s/209s"),
        )

        //This one according to the requirements should always have a lever.
        const cWallMounted2HoleWithBackplate = createPriceContainer(
            "Wall Mounted 2 Hole, Square Plate",
            "Wall Mounted 2 Hole, Square Plate",
            tapNodes("Safari Lever Wall Mounted Basin Mixer with Square Backplate", mixerAmountGroup, ["Pressure"]),
            variantSelected(TapCounter, "109s-sq/209s-sq"),
        )

        return createPriceContainer(
            "Countertop Mixers",
            "Countertop Mixers",
            [cExtendedMixers, cWallMounted3Hole, cWallMounted2Hole, cWallMounted2HoleWithBackplate],
            countertopSelected(),
        )
    }

    const createCountertopBasins = (): PriceContainer => {
        const shape = (description: string, terms: string, variantId: string) =>
            createPriceContainer(
                description,
                description,
                createKeyedVariantPriceNodes(catalog, terms, 4, basinFinishCountertopVariants, {amount: mixerAmountGroup}),
                variantSelected(BasinShape, variantId),
            )

        return createPriceContainer(
            "Countertop Basins",
            "Countertop Basins",
            [
                shape("Round", "Safari Smooth Countertop Basin - Round", "round"),
                shape("Round Fluted", "Safari Fluted Countertop Basin - Round", "round_fluted"),
                shape("Square", "Safari Smooth Countertop Basin - Square", "square"),
            ],
            countertopSelected(),
        )
    }

    //---------------------------------------------Bottle trap---------------------------------------------

    //TODO: the bottle trap is missing in the October 2026 price list; add createBottleTrap() to the price containers again once it is back
    const createBottleTrap = (): PriceContainer =>
        createPriceContainer(
            "Bottle Trap",
            "Bottle Trap",
            createKeyedVariantPriceNodes(catalog, "Round Bottle Trap", 4, bottleTrapFinishVariants, {amount: mixerAmountGroup}),
            variantSelected(BottleTrap, "bottle_trap_with"),
        )

    //---------------------------------------------Overflow covers---------------------------------------------

    const createOverflowCovers = (): PriceContainer => {
        //Matt White is not offered for Safari
        const coverNodes = (terms: string) =>
            createKeyedVariantPriceNodes(catalog, terms, 4, overflowCoverFinishVariants, {amount: mixerAmountGroup, termExclude: ["Matt White"]})

        const cCeramicCover = createPriceContainer(
            "Ceramic Cover",
            "Ceramic Cover",
            coverNodes("Ceramic Basin Overflow Cover"),
            variantSelected(BasinOverflow, "with_overflow"),
        )

        const cMineralCover = createPriceContainer(
            "Mineral Cover",
            "Mineral Cover",
            coverNodes("Mineral Basin Overflow Cover"),
            or(
                variantSelected(BasinDesign400_500, "mineral_400-500"),
                variantSelected(BasinDesign600_800, "mineral_600-800"),
                variantSelected(BasinDesign1000_1200, "mineral_1000-1200"),
            ),
        )

        return createPriceContainer("Overflow Cover", "Overflow Cover", [cCeramicCover, cMineralCover], not(countertopSelected()))
    }

    result.priceContainers.push(
        create400Container(),
        create500Container(),
        create600Container(),
        create800Container(),
        create1000Container(),
        create1200Container(),
        createCountertopBasins(),
        createMiniMixers(),
        createMixers(),
        createCountertopMixers(),
        createUniversalBasinWaste(),
        createHandles(),
        //TODO: bottle trap and joining screws are missing in the October 2026 price list, re-enable once they are back
        // createBottleTrap(),
        createOverflowCovers(),
        // createScrews(),
    )

    return result
}
